import React, { useState, useEffect } from 'react';
import { Modal, Button, Dropdown, Spinner } from 'react-bootstrap';
import { X, Plus, Trash, ExclamationTriangle, CreditCard, InfoCircle, CheckCircle } from 'react-bootstrap-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';
import './MultiplePaymentsModal.scss';
import { formatCurrency } from '../../../../../../../../../../../utils/functions';
import { updatePaymentReceiptSuggestedTip } from '../../../../../../../../../../../crud/payments_api/payment_receipts';
import { toast } from 'react-toastify';

interface PaymentMethod {
  payment_type_id: number;
  payment_type_name: string;
  is_custom: boolean;
  custom_payment_type_id?: number;
}

interface PaymentEntry {
  id: number;
  payment_method_id: number | null;
  payment_method_name: string;
  amount: number;
  custom_payment_type_id?: number;
  tip_amount?: number; // New field for tip per payment method
}

interface MultiplePaymentsModalProps {
  show: boolean;
  onHide: () => void;
  paymentMethods: PaymentMethod[];
  totalDue: number;
  onConfirm: (payments: PaymentEntry[], newTip?: number) => void;
  initialPayments?: PaymentEntry[];
  suggestedTip?: number; // New prop for suggested tip
  receiptId: number; // Receipt ID for updating tip
  receipts: any[]; // Current receipts state
  setReceipts: (receipts: any[]) => void; // Function to update receipts state
}

const MultiplePaymentsModal: React.FC<MultiplePaymentsModalProps> = ({
  show,
  onHide,
  paymentMethods,
  totalDue,
  onConfirm,
  initialPayments = [],
  suggestedTip = 0,
  receiptId,
  receipts,
  setReceipts
}) => {
  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [nextId, setNextId] = useState(1);
  const [isUpdatingTip, setIsUpdatingTip] = useState(false);
  const [currentSuggestedTip, setCurrentSuggestedTip] = useState(suggestedTip);
  const [tipUpdateSuccess, setTipUpdateSuccess] = useState(false);
  const [hasInternalTipUpdate, setHasInternalTipUpdate] = useState(false); // Track if tip was updated from within modal

  // Calculate tip rate from suggested tip and total due
  const calculateTipRate = () => {
    if (totalDue <= 0 || suggestedTip <= 0) return 0;
    return suggestedTip / totalDue;
  };

  const [tipRate, setTipRate] = useState(calculateTipRate());

  useEffect(() => {
    if (show) {
      // Only reset if this is a fresh open (not an internal tip update)
      if (!hasInternalTipUpdate) {
        setCurrentSuggestedTip(suggestedTip);
        setTipUpdateSuccess(false);
        
        if (initialPayments.length > 0) {
          setPayments(initialPayments);
          setNextId(Math.max(...initialPayments.map(p => p.id)) + 1);
        } else {
          // Calculate tip rate and amounts correctly
          // totalDue already includes the tip in it
          // suggestedTip is the tip portion
          const netAmount = totalDue - suggestedTip;
          const calculatedTipRate = netAmount > 0 ? suggestedTip / netAmount : 0;
          
          const initialPayment: PaymentEntry = {
            id: 1,
            payment_method_id: null,
            payment_method_name: 'Seleccionar método',
            amount: Math.round(netAmount),
            tip_amount: Math.round(suggestedTip)
          };
          
          setPayments([initialPayment]);
          setNextId(2);
          setTipRate(calculatedTipRate);
        }
      }
    } else {
      // Reset the internal update flag when modal closes
      setHasInternalTipUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initialPayments.length, totalDue, suggestedTip]);

  const addPaymentEntry = () => {
    // Calculate the expected net amount (total minus tip)
    const expectedNetAmount = totalDue - suggestedTip;
    // Calculate remaining net amount needed to reach the expected net
    const remainingNetAmount = Math.max(0, expectedNetAmount - getTotalAmount());
    // Calculate remaining tip needed to reach the expected tip
    const remainingTip = Math.max(0, suggestedTip - getTotalTipAmount());
    
    const newPayment: PaymentEntry = {
      id: nextId,
      payment_method_id: null,
      payment_method_name: 'Seleccionar método',
      amount: remainingNetAmount,
      tip_amount: remainingTip
    };
    setPayments([...payments, newPayment]);
    setNextId(nextId + 1);
  };

  const removePaymentEntry = (id: number) => {
    if (payments.length > 1) {
      setPayments(payments.filter(p => p.id !== id));
    }
  };

  const updatePaymentMethod = (id: number, method: PaymentMethod) => {
    setPayments(payments.map(p => 
      p.id === id 
        ? { 
            ...p, 
            payment_method_id: method.payment_type_id, 
            payment_method_name: method.payment_type_name,
            custom_payment_type_id: method.is_custom ? method.custom_payment_type_id : undefined
          }
        : p
    ));
  };

  const updatePaymentAmount = (id: number, amount: number) => {
    const newAmount = Math.max(0, amount);
    // Calculate proportional tip based on the tip rate
    const proportionalTip = Math.round(newAmount * tipRate);
    
    setPayments(payments.map(p => 
      p.id === id 
        ? { 
            ...p, 
            amount: newAmount,
            tip_amount: proportionalTip
          }
        : p
    ));
  };

  const updatePaymentTip = (id: number, tipAmount: number) => {
    setPayments(payments.map(p => 
      p.id === id 
        ? { 
            ...p, 
            tip_amount: Math.max(0, tipAmount)
          }
        : p
    ));
  };

  const getTotalAmount = () => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getTotalTipAmount = () => {
    return payments.reduce((sum, payment) => sum + (payment.tip_amount || 0), 0);
  };

  // Check if tip amounts don't match the declared tip (for showing update prompt)
  const getTipMismatch = () => {
    const enteredTip = getTotalTipAmount();
    const difference = enteredTip - currentSuggestedTip;
    return {
      hasMismatch: Math.abs(difference) > 0.01,
      enteredTip,
      expectedTip: currentSuggestedTip,
      difference
    };
  };

  const isValidForConfirmation = () => {
    const netTotal = totalDue - currentSuggestedTip;
    const allMethodsSelected = payments.every(p => p.payment_method_id !== null);
    const allAmountsValid = payments.every(p => p.amount >= 0); // Allow 0 for tip-only payments
    const amountsMatch = getTotalAmount() === netTotal;
    const tipsMatch = getTotalTipAmount() === currentSuggestedTip;
    
    return allMethodsSelected && allAmountsValid && amountsMatch && tipsMatch;
  };

  // Handler to update the tip in the backend
  const handleUpdateTip = async () => {
    const newTipAmount = getTotalTipAmount();
    setIsUpdatingTip(true);
    setHasInternalTipUpdate(true); // Prevent useEffect from resetting payments
    
    try {
      await updatePaymentReceiptSuggestedTip(receiptId, newTipAmount);
      
      // Update local state
      setCurrentSuggestedTip(newTipAmount);
      setTipUpdateSuccess(true);
      
      // Update receipts state
      const updatedReceipts = receipts.map((r: any) =>
        r.receipt_id === receiptId
          ? { ...r, suggested_tip: newTipAmount }
          : r
      );
      setReceipts(updatedReceipts);
      
      toast.success('Propina actualizada correctamente');
      
      // Clear success message after a moment
      setTimeout(() => setTipUpdateSuccess(false), 2000);
    } catch (error) {
      console.error('Error updating tip:', error);
      toast.error('Error al actualizar la propina');
    } finally {
      setIsUpdatingTip(false);
    }
  };

  const handleConfirm = () => {
    if (isValidForConfirmation()) {
      // Send amounts and tips separately to backend
      const paymentsForBackend = payments.map(payment => ({
        ...payment,
        amount: payment.amount, // Send net amount only
        tip_amount: payment.tip_amount || 0 // Send tip separately
      }));
      onConfirm(paymentsForBackend, currentSuggestedTip);
      onHide();
    }
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      centered
      size="xl"
      className="multiple-payments-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            <CreditCard />
            Múltiples Métodos de Pago
          </Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        <div className="total-due-section">
          <div className="total-due-info">
            <span className="total-due-label">Total a pagar:</span>
            <span className="total-due-amount">{formatCurrency(totalDue)}</span>
          </div>
        </div>

        <div className="payments-list">
          {payments.map((payment, index) => (
            <div key={payment.id} className="payment-entry">
              <div className="payment-entry-row payment-method-row">
                <span className="payment-number">#{index + 1}</span>
                
                <Dropdown className="payment-method-dropdown">
                  <Dropdown.Toggle variant="none" className="payment-method-toggle">
                    {payment.payment_method_name}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {paymentMethods.map(method => (
                      <Dropdown.Item 
                        key={method.payment_type_id}
                        onClick={() => updatePaymentMethod(payment.id, method)}
                      >
                        {method.payment_type_name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {payments.length > 1 && (
                  <button
                    className="remove-payment-btn"
                    onClick={() => removePaymentEntry(payment.id)}
                    title="Eliminar método de pago"
                  >
                    <Trash />
                  </button>
                )}
              </div>

              <div className="payment-entry-row amount-row">
                <div className="amount-tip-container">
                <div className="amount-input-group">
                  <div className="currency-symbol">$</div>
                  <input
                    type="text"
                    className="amount-input"
                    value={
                      payment.amount > 0
                        ? payment.amount.toLocaleString('es-CL', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : ''
                    }
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const cleanedValue = inputValue.replace(/\D/g, '');
                      const numValue = cleanedValue === '' ? 0 : parseInt(cleanedValue, 10);
                      updatePaymentAmount(payment.id, numValue);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+' || e.key.toLowerCase() === 'e') {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Monto"
                  />
                </div>

                <div className="amount-input-group tip-input">
                  <div className="currency-symbol tip-symbol">
                    <FontAwesomeIcon icon={faGift} />
                  </div>
                  <input
                    type="text"
                    className="amount-input"
                    value={
                      payment.tip_amount !== undefined && payment.tip_amount !== null && payment.tip_amount > 0
                        ? payment.tip_amount.toLocaleString('es-CL', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })
                        : ''
                    }
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const cleanedValue = inputValue.replace(/\D/g, '');
                      const numValue = cleanedValue === '' ? 0 : parseInt(cleanedValue, 10);
                      updatePaymentTip(payment.id, numValue);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === ',' || e.key === '-' || e.key === '+' || e.key.toLowerCase() === 'e') {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Propina"
                    title="Ingrese el monto de propina para este método de pago"
                  />
                </div>
              </div>
              </div>
            </div>
          ))}
        </div>

        <div className="add-payment-section">
          <button
            className="add-payment-btn"
            onClick={addPaymentEntry}
          >
            <Plus />
            Añadir otro método
          </button>
        </div>

        {/* Tip Mismatch Banner - Shows when entered tip differs from declared tip */}
        {getTipMismatch().hasMismatch && !tipUpdateSuccess && (
          <div className="tip-update-banner">
            <div className="tip-update-banner-content">
              <InfoCircle className="info-icon" />
              <div className="tip-update-text">
                <span className="tip-update-title">La propina ingresada es diferente</span>
                <span className="tip-update-details">
                  Ingresaste <strong>{formatCurrency(getTipMismatch().enteredTip)}</strong> pero la propina declarada es <strong>{formatCurrency(getTipMismatch().expectedTip)}</strong>
                </span>
              </div>
            </div>
            <Button
              variant="none"
              className="tip-update-button"
              onClick={handleUpdateTip}
              disabled={isUpdatingTip}
            >
              {isUpdatingTip ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Actualizando...
                </>
              ) : (
                <>
                  Actualizar propina a {formatCurrency(getTipMismatch().enteredTip)}
                </>
              )}
            </Button>
          </div>
        )}

        {/* Success Banner - Shows briefly after tip update */}
        {tipUpdateSuccess && (
          <div className="tip-update-success-banner">
            <CheckCircle className="success-icon" />
            <span>¡Propina actualizada correctamente!</span>
          </div>
        )}

        <div className="payment-summary">
          <div className="summary-row">
            <span className="summary-label">Total pagos (neto):</span>
            <span className="summary-amount">{formatCurrency(getTotalAmount())}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Esperado (neto):</span>
            <span className="summary-amount">{formatCurrency(totalDue - currentSuggestedTip)}</span>
          </div>
          <div className={`summary-row ${getTotalAmount() === (totalDue - currentSuggestedTip) ? 'balanced' : 'unbalanced'}`}>
            <span className="summary-label">Diferencia (neto):</span>
            <span className="summary-amount">{formatCurrency(Math.abs(getTotalAmount() - (totalDue - currentSuggestedTip)))}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Total propinas:</span>
            <span className="summary-amount">{formatCurrency(getTotalTipAmount())}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">Propina esperada:</span>
            <span className="summary-amount">{formatCurrency(currentSuggestedTip)}</span>
          </div>
          <div className={`summary-row ${getTotalTipAmount() === currentSuggestedTip ? 'balanced' : 'unbalanced'}`}>
            <span className="summary-label">Diferencia (propina):</span>
            <span className="summary-amount">{formatCurrency(Math.abs(getTotalTipAmount() - currentSuggestedTip))}</span>
          </div>
          <div className="summary-row total-pay">
            <span className="summary-label">Total general:</span>
            <span className="summary-amount">{formatCurrency(getTotalAmount() + getTotalTipAmount())}</span>
          </div>
        </div>

        {/* Other Validation Warnings */}
        {!isValidForConfirmation() && !getTipMismatch().hasMismatch && (
          <div className="validation-warning">
            <ExclamationTriangle className="warning-icon" />
            <div className="warning-content">
              {getTotalAmount() !== (totalDue - currentSuggestedTip) && (
                <div className="warning-message">
                  {getTotalAmount() < (totalDue - currentSuggestedTip)
                    ? `Faltan ${formatCurrency((totalDue - currentSuggestedTip) - getTotalAmount())} en montos (neto)`
                    : `Hay ${formatCurrency(getTotalAmount() - (totalDue - currentSuggestedTip))} de más en montos (neto)`
                  }
                </div>
              )}
              {payments.some(p => p.payment_method_id === null) && (
                <div className="warning-message">
                  Debe seleccionar un método de pago para todas las entradas
                </div>
              )}
              {payments.some(p => p.amount < 0) && (
                <div className="warning-message">
                  Los montos no pueden ser negativos
                </div>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            variant="none"
            className="form-button-confirm"
            onClick={handleConfirm}
            disabled={!isValidForConfirmation()}
          >
            Confirmar Pagos
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MultiplePaymentsModal;
